import { createContext, useContext, useMemo } from "react";

import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

interface Context {
  isOpen: boolean;
  open: () => void;
}

const ForbiddenPopupContext = createContext<Context>(null!);

export function useForbiddenPopupContext(): Context {
  return useContext(ForbiddenPopupContext);
}

export function ForbiddenPopupProvider({ children }: React.PropsWithChildren<{}>): React.ReactNode {
  const { t } = useTranslation();
  const [isModalOpened, modalHandler] = useBool(false);

  const contextValue = useMemo(
    () => ({
      isOpen: isModalOpened,
      open: modalHandler.setTrue,
    }),
    [isModalOpened, modalHandler],
  );

  return (
    <ForbiddenPopupContext value={contextValue}>
      <Modal.Root
        isOpened={isModalOpened}
        title={t("component.forbidden-user-details-modal.title")}
        description={t("component.forbidden-user-details-modal.description")}
        size="sm"
        onOpenChange={modalHandler.set}
      >
        <Modal.Actions>
          <Modal.Close>
            <Button>{t("common.action.close")}</Button>
          </Modal.Close>
        </Modal.Actions>
      </Modal.Root>

      {children}
    </ForbiddenPopupContext>
  );
}
